import React from "react";
import { motion } from "framer-motion";

/**
 * Assets metric tile matching the premium reference layout:
 * featured card = centered icon, value, title, hint;
 * standard cards = title + icon on top, value, optional progress bar.
 */
const AssetMetricCard = ({
  title,
  value,
  icon: Icon,
  color,
  hint,
  progress = null,
  featured = false,
}) => {
  const iconBg = `color-mix(in srgb, ${color} 12%, var(--color-bg-card))`;
  const trackBg = `color-mix(in srgb, ${color} 12%, var(--color-border))`;
  const featuredBg = `color-mix(in srgb, ${color} 4%, var(--color-bg-card))`;
  const featuredBorder = "var(--color-border)";
  const featuredTitleColor = "var(--color-text-light)";

  if (featured) {
    return (
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ type: "spring", stiffness: 400, damping: 28 }}
        style={{ height: "100%" }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 22,
            padding: "28px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            background: featuredBg,
            border: `1px solid ${featuredBorder}`,
            boxShadow: "0 8px 24px color-mix(in srgb, var(--color-text) 5%, transparent)",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--color-bg-card)",
              boxShadow: "0 4px 14px color-mix(in srgb, var(--color-text) 8%, transparent)",
              flexShrink: 0,
            }}
          >
            <Icon size={22} color={color} />
          </div>

          <div
            style={{
              marginTop: 20,
              fontSize: 64,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.045em",
              color: "var(--color-text)",
              fontFamily: '"Segoe UI Variable Display", "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
            }}
          >
            {value}
          </div>

          <div
            style={{
              marginTop: 14,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: featuredTitleColor,
              lineHeight: 1.4,
            }}
          >
            {title}
          </div>

          {hint && (
            <div
              style={{
                marginTop: 6,
                fontSize: 10,
                fontWeight: 500,
                color: "var(--color-text-light)",
                lineHeight: 1.35,
                whiteSpace: "nowrap",
              }}
            >
              {hint}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      style={{ height: "100%" }}
    >
      <div
        style={{
          height: "100%",
          minHeight: 148,
          borderRadius: 22,
          padding: "20px 22px 18px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-border)",
          boxShadow: "0 8px 24px color-mix(in srgb, var(--color-text) 5%, transparent)",
        }}
      >
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--color-text-light)",
              lineHeight: 1.4,
              paddingTop: 4,
            }}
          >
            {title}
          </div>

          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: iconBg,
            }}
          >
            <Icon size={15} color={color} />
          </div>
        </div>

        <div
          style={{
            marginTop: 18,
          }}
        >
          <div
            style={{
              fontSize: 34,
              fontWeight: 800,
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              color: "var(--color-text)",
            }}
          >
            {value}
          </div>

          {hint && (
            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                fontWeight: 500,
                color: "var(--color-text-light)",
                lineHeight: 1.35,
              }}
            >
              {hint}
            </div>
          )}
        </div>

        {progress !== null && progress !== undefined && (
          <div style={{ marginTop: "auto", paddingTop: 16 }}>
            <div
              style={{
                height: 7,
                borderRadius: 999,
                background: trackBg,
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                transition={{ duration: 0.65, ease: "easeOut" }}
                style={{
                  height: "100%",
                  borderRadius: 999,
                  background: color,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AssetMetricCard;
